import { useEffect, useRef, useState } from "react";
import { Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { PROPERTY_GALLERY } from "@/mock";

const Gallery = () => {
  const [active, setActive] = useState(null); // index of opened photo
  const close = () => setActive(null);

  // Keyboard navigation
  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight")
        setActive((i) => (i + 1) % PROPERTY_GALLERY.length);
      if (e.key === "ArrowLeft")
        setActive((i) => (i - 1 + PROPERTY_GALLERY.length) % PROPERTY_GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  return (
    <section
      id="gallery"
      className="py-20 md:py-28 px-5 md:px-10 bg-white"
      data-testid="gallery-section"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-3">
            <span className="h-[1px] w-6 bg-[#C8A97E]" />
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#C8A97E] font-semibold inline-flex items-center gap-2">
              <Camera className="w-3 h-3" /> Property Gallery
            </span>
            <span className="h-[1px] w-6 bg-[#C8A97E]" />
          </div>
          <h2 className="font-serif text-[40px] sm:text-[46px] font-semibold text-[#1A1A1A] leading-[1.1] mb-3">
            Take a <em className="text-[#C8A97E] font-normal">closer look</em>
          </h2>
          <p className="text-[15px] text-[#666] max-w-xl mx-auto leading-relaxed">
            Real photos of our property — exterior, lobby, rooms and amenities.
          </p>
        </div>

        {/* Bento grid layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[200px]">
          {PROPERTY_GALLERY.map((p, i) => {
            // Bento sizing: alternate sizes for visual interest
            const sizeClass =
              i === 0
                ? "col-span-2 row-span-2"
                : i === 3
                ? "row-span-2"
                : i === 5
                ? "col-span-2"
                : "";
            return (
              <button
                key={p.url}
                onClick={() => setActive(i)}
                className={`relative overflow-hidden rounded-[16px] group ${sizeClass}`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img
                  src={p.url}
                  alt={p.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-3 left-4 text-[11px] tracking-[0.18em] uppercase text-white font-medium">
                  {p.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {active !== null && (
        <div
          className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={close}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i - 1 + PROPERTY_GALLERY.length) % PROPERTY_GALLERY.length);
            }}
            className="absolute left-3 md:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActive((i) => (i + 1) % PROPERTY_GALLERY.length);
            }}
            className="absolute right-3 md:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center text-white"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div
            className="max-w-5xl w-full max-h-[88vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={PROPERTY_GALLERY[active].url}
              alt={PROPERTY_GALLERY[active].label}
              className="max-w-full max-h-[80vh] rounded-[14px] object-contain"
            />
            <div className="text-white/80 text-[13px] mt-4 tracking-wide">
              {PROPERTY_GALLERY[active].label} · {active + 1} / {PROPERTY_GALLERY.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
